import { useEffect, useRef, useState } from 'react';

import { Button, Dropdown, Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { unique } from '@revenuemarket/utils/arrayUtils';
import { Typography } from 'antd';
import { GroupTreeItem } from '~apis/custom-label';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { css } from '~libs/emotion';
import { CashFlowType } from '~models/cashFlow';

import { filterSearchTreeList } from '../common/utils';
import GroupTreeDropDown from './GroupTreeDropDown';
// TODO: Refactor to resolve circular dependency
// oxlint-disable-next-line import/no-cycle
import GroupTreeDropDownItem from './GroupTreeDropDownItem';

export const getDepthStyle = (depth: number) => {
  switch (depth) {
    case 0:
      return css`
        padding-left: 0;
        color: ${Colors.gray400};
      `;

    default:
      return css`
        padding-left: ${depth * 16}px;
        color: ${Colors.gray900};
      `;
  }
};

export const EMPTY_GROUP_LABEL_KEY = '0';

interface Props {
  transactionId?: number;
  triggerElement?: React.ReactNode;
  footerElement?: React.ReactNode;
  treeList?: FlattenTreeResult<GroupTreeItem>[];
  style?: React.CSSProperties;
  defaultShow?: boolean;
  closeOnSelect?: boolean;
  onSelect?: (value: string | number, key: string[]) => void;
  onApply?: (item?: FlattenTreeResult<GroupTreeItem>, selctedKey?: string[]) => void;
  onClear?: () => void;
  multiple?: boolean;
  defaultActiveKey?: string[];
  applyLoading?: boolean;
  cashFlowType?: CashFlowType;
  treeRef?: React.RefObject<HTMLDivElement>;
  wrapperRef?: React.RefObject<HTMLDivElement>;
  additionalRef?: React.RefObject<HTMLDivElement>;
  /** 비활성화 아이템도 action이 가능히게 하려면 disableActionOnInactiveItem = false (ex: 분류 관리 page) */
  disableActionOnInactiveItem?: boolean;
  dropDownStyle?: React.CSSProperties;
  headerElement?: React.ReactNode;
  showReset?: boolean;
  onDropdownOpenChange?: (open: boolean) => void;
  disableScroll?: boolean;
  getPopupContainer?: () => HTMLElement;
}

const GroupTreeSelect: React.FC<Props> = ({
  headerElement,
  triggerElement,
  treeList = [],
  footerElement,
  defaultShow,
  defaultActiveKey,
  closeOnSelect = false,
  onApply,
  onClear,
  onSelect,
  applyLoading,
  cashFlowType,
  treeRef,
  wrapperRef,
  additionalRef,
  // 비활성화 아이템도 action이 가능히게 하려면 disableActionOnInactiveItem = false (ex: 분류 관리 page)
  disableActionOnInactiveItem = true,
  multiple = false,
  dropDownStyle,
  showReset = false,
  onDropdownOpenChange,
  disableScroll,
  getPopupContainer,
}) => {
  const [activeKey, setActiveKey] = useState<string[] | null>(defaultActiveKey ?? null);

  const [show, setShow] = useState(defaultShow ?? false);
  const [searchValue, setSearchValue] = useState('');
  const inputRef = useRef<any>(null); // Input ref 생성

  const handleActiveKey = ({
    targetKey: key,
    node,
    selected,
  }: {
    targetKey: string;
    node: FlattenTreeResult<GroupTreeItem>;
    selected?: boolean;
  }) => {
    const targetKey = key;
    const childrenTree = flattenTree(node?.children ?? []);
    if (!node.value?.isActive && disableActionOnInactiveItem) return;
    if (!selected) {
      const targetTree = [...childrenTree.map((item) => item.key), targetKey];

      const activeKeys = unique([...(activeKey ?? []), ...targetTree]).filter(Boolean);

      const target = node?.value?.id.toString();

      if (multiple) {
        onSelect?.(target ?? '', activeKeys);
        setActiveKey(activeKeys);
      } else {
        onSelect?.(target ?? '', [targetKey]);
        setActiveKey([targetKey]);
      }
    } else {
      const targetTree = [...childrenTree.map((item) => item.key), targetKey];
      const activeKeys = (activeKey ?? [])
        .filter((key) => !targetTree.includes(key))
        .filter(Boolean);
      const selectedValue = node.value?.id.toString();
      if (multiple) {
        onSelect?.(selectedValue ?? '', activeKeys);
        setActiveKey(activeKeys);
      } else {
        onSelect?.(selectedValue ?? '', []);
        setActiveKey([]);
      }
    }
  };

  const handleTreeItemClick = (key: string) => {
    const selected = treeList.find((node) => node.value?.id.toString() === key);
    if (!selected?.value?.isActive && disableActionOnInactiveItem) return;
    if (multiple && selected) {
      handleActiveKey({
        targetKey: key,
        node: selected,
        selected: activeKey?.includes(key),
      });
      return;
    }

    if (!selected?.value?.isActive && disableActionOnInactiveItem) {
      return;
    }

    if (closeOnSelect) {
      setShow(false);
    }
    const selectedValue = selected?.value?.id.toString();
    if (multiple) {
      const selectedKey = unique([...(activeKey ?? []), key.toString()]);
      setActiveKey(selectedKey);

      if (selectedValue) {
        onSelect?.(selectedValue, selectedKey);
      }
    } else {
      const selectedKey = [key];
      setActiveKey(selectedKey);

      if (selectedValue) {
        onSelect?.(selectedValue, selectedKey);
      }
    }
  };

  useEffect(() => {
    if (defaultActiveKey && treeRef?.current) {
      setActiveKey(defaultActiveKey);
      // 해당 cell로 scroll
      const selectedNode = treeRef?.current?.querySelector(`[data-key="${defaultActiveKey[0]}"]`);

      if (selectedNode) {
        setTimeout(() => {
          selectedNode.scrollIntoView({
            block: 'center',
            inline: 'center',
          });
        }, 100);
      }
    }
  }, [defaultActiveKey, treeRef]);

  useEffect(() => {
    setShow(defaultShow ?? false);
  }, [defaultShow]);

  useEffect(() => {
    if (show && inputRef.current) {
      // 드롭다운이 열릴 때 Input에 포커스 설정
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
    if (show === false) {
      // 드롭다운이 닫힐 때 Input에 포커스 해제
      setSearchValue('');
    }
  }, [show]);

  const searchResults = filterSearchTreeList<GroupTreeItem>(treeList, searchValue);

  const groupOptionList = searchValue ? searchResults : treeList;

  const optionList =
    showReset && !searchValue
      ? [
          {
            key: EMPTY_GROUP_LABEL_KEY,
            isDisabled: false,
            children: [],
            value: {
              name: '빈 값',
              id: EMPTY_GROUP_LABEL_KEY,
              isActive: true,
            },
          },
          ...groupOptionList,
        ]
      : groupOptionList;

  const handleSelectAllFiltered = () => {
    if (!multiple) return;

    const filteredKeys = optionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .map((node) => node.key);

    const childrenTree = optionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .flatMap((node) => flattenTree(node?.children ?? []).map((item) => item.key));

    const allKeys = unique([...(activeKey || []), ...filteredKeys, ...childrenTree]);

    setActiveKey(allKeys);

    // Trigger onSelect for each filtered option
    optionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .forEach((node) => {
        onSelect?.(node.value?.id.toString() || '', allKeys);
      });
  };

  const handleClearAllFiltered = () => {
    if (!multiple) return;

    const filteredKeys = optionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .map((node) => node.key);

    const childrenTree = optionList
      .filter((node) => node.value?.isActive && !node.isDisabled)
      .flatMap((node) => flattenTree(node?.children ?? []).map((item) => item.key));

    const keysToRemove = [...filteredKeys, ...childrenTree];
    const newActiveKeys = (activeKey || []).filter((key) => !keysToRemove.includes(key));

    setActiveKey(newActiveKeys);

    // Trigger onSelect for clearing
    if (optionList.length > 0) {
      const firstNode = optionList.find((node) => node.value?.isActive && !node.isDisabled);
      onSelect?.(firstNode?.value?.id.toString() || '', newActiveKeys);
    }
  };

  return (
    <Dropdown
      disableScroll={disableScroll}
      trigger={['click']}
      defaultOpen={defaultShow}
      onDropdownOpenChange={(open) => {
        onDropdownOpenChange?.(open);
        if (!open) {
          onClear?.();
          setActiveKey([]);
          setSearchValue('');
        } else {
          setActiveKey((prev) =>
            prev === null || prev.length === 0 ? (defaultActiveKey ?? []) : prev,
          );
        }
      }}
      placement="bottomLeft"
      getPopupContainer={getPopupContainer}
      dropdownRender={(menu) => (
        <div
          ref={wrapperRef}
          style={{
            width: '100%',
          }}
        >
          <GroupTreeDropDown
            wrapperRef={wrapperRef}
            headerElement={headerElement}
            menu={menu}
            treeList={groupOptionList}
            activeKey={activeKey}
            multiple={multiple}
            setActiveKey={setActiveKey}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            inputRef={inputRef}
            footerElement={footerElement}
            treeRef={treeRef}
            setShow={setShow}
            show={show}
            dropDownStyle={dropDownStyle}
            onApply={onApply}
            onClear={onClear}
            applyLoading={applyLoading}
            disableActionOnInactiveItem={disableActionOnInactiveItem}
            cashFlowType={cashFlowType}
            onSelectAll={handleSelectAllFiltered}
            onClearAllSelect={handleClearAllFiltered}
          />
        </div>
      )}
      menu={{
        //@ts-ignore
        items: [
          ...optionList.map((node, index) => {
            const disabled = node?.isDisabled || !node.value?.isActive;

            return {
              key: node.key + (disabled ? '-group' : ''),
              type: disabled ? 'group' : 'item',
              id: node.key + '',
              label: (
                <GroupTreeDropDownItem
                  key={node.key}
                  multiple={multiple}
                  targetIndex={index}
                  // @ts-ignore
                  node={node}
                  onSelect={onSelect}
                  activeKey={activeKey ?? []}
                  handleActiveKey={handleActiveKey}
                  closeOnSelect={closeOnSelect}
                  setActiveKey={setActiveKey}
                  disabled={disabled}
                />
              ),
            };
          }),
        ],
        onKeyDownCapture: (e) => {
          if (e.key === 'Enter') {
            if (!closeOnSelect) {
              e.stopPropagation();
              const targetKey = (e.target as HTMLElement).getAttribute('id');
              if (targetKey) handleTreeItemClick(targetKey);
            }
          }
        },
        onClick: (item) => {
          handleTreeItemClick(item.key);
        },
      }}
    >
      <Flex style={{ width: '100%' }} onClick={() => setShow(true)}>
        {triggerElement || (
          <Button disable={treeList.length === 0}>
            <Typography
              style={{
                ...typographyStyle['14_400'],
              }}
            >
              {' '}
              바꾸기
            </Typography>
          </Button>
        )}
      </Flex>
    </Dropdown>
  );
};

export default GroupTreeSelect;
